import log from 'loglevel';
import posthog from 'posthog-js';
import { NODE_ENV } from '../api-config.mjs';
import { identifyDevice, pageLoadEvent, recordAnalyticsEvents } from 'api.mjs';
import { getCookie, isBrowser } from './Browser';
import { getSessionFromStorage } from './Session';

// Control analytics on/off when local testing
const DISABLE_ANALYTICS_IN_DEV = false;
let posthogInitialized = false;
const MAX_LOCAL_EVENTS = 50;
const FLUSH_WAIT_TIME = 5000;  // 5 seconds
let events = null;
const pageLoads = [];
let hasWindowLoaded = false;
let flushTimer = null;
let lastPageLoaded = null;
let refs = null;

const decodeReferrerHash = (hash) => {
  if (!hash) {
    return;
  }
  try {
    const decodedString = atob(hash.substring(1));
    const data = JSON.parse(decodedString);
    refs = data.refs;
  } catch (err) {
    log.debug(err);
  }
};

// https://stackoverflow.com/a/54246501
const camelToSnakeCase = str => str[0].toLowerCase() + str.slice(1, str.length).replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

// Properties nested under `excludeFromGtag` fan out to every analytics sink we
// own — PostHog and the backend store (Elasticsearch) — but are withheld from
// Google Analytics. Use this for high-cardinality / free-text values (e.g. a
// customer's typed cancellation reason): GA's dimension cardinality cap degrades
// such values into "(other)" and GA's policy forbids the PII free text can carry,
// whereas ClickHouse (PostHog) and Elasticsearch handle them fine.
const GTAG_EXCLUDED_KEY = "excludeFromGtag";

// For sinks that DO receive the excluded props (PostHog, Elasticsearch): flatten
// the bag's keys up to the top level alongside the shared properties.
const flattenExcluded = (event) => {
  if (!event || !event[GTAG_EXCLUDED_KEY]) {
    return event;
  }
  const { [GTAG_EXCLUDED_KEY]: excluded, ...rest } = event;
  return { ...rest, ...excluded };
};

// For Google Analytics: drop the bag and everything inside it.
const withoutGtagExcluded = (event) => {
  if (!event || !event[GTAG_EXCLUDED_KEY]) {
    return event;
  }
  const { [GTAG_EXCLUDED_KEY]: _omit, ...rest } = event;
  return rest;
};

const gTagEvent = (event) => {
  const eventName = camelToSnakeCase(event.name);
  // https://www.gatsbyjs.com/plugins/gatsby-plugin-google-gtag/#custom-events
  // https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
  window.gtag("event", eventName, withoutGtagExcluded(event));
};

const sendEventsToGTag = ({ events }) => {
  if (!events || !events.length) {
    return;
  }
  if (typeof window === "undefined" || typeof window.gtag === "undefined") {
    return;
  }
  events.forEach(event => {
    gTagEvent(event);
  });
};

// PostHog event forwarding — sends events via posthog.capture()
// Docs: https://posthog.com/docs/libraries/js#capturing-events
const sendEventsToPostHog = ({ events }) => {
  if (!events || !events.length) {
    return;
  }
  if (!posthogInitialized) {
    return;
  }
  events.forEach(event => {
    const eventName = camelToSnakeCase(event.name);
    posthog.capture(eventName, flattenExcluded(event));
  });
};

const flushEvents = () => {
  log.debug("flushEvents()");
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  localStorage.removeItem("events");
  const { navigator: { userAgent } } = window;
  const sessionContext = getSessionFromStorage();
  // The backend store (Elasticsearch) receives the excluded props too — only GA is withheld.
  recordAnalyticsEvents({ data: { events: events.map(flattenExcluded), refs, userAgent }, context: {sessionContext: sessionContext} })
    .catch(err => {
      log.error("Failed to send analytics events.");
      log.error(err);
    });
  sendEventsToGTag({ events });
  sendEventsToPostHog({ events });
  events = [];
};

const recordEvent = (event) => {
  if (!isBrowser()) {
    return;
  }
  if (DISABLE_ANALYTICS_IN_DEV && NODE_ENV === "development") {
    return;
  }
  if (!events) {
    events = JSON.parse(localStorage.getItem("events")) || [];
  }
  log.debug("recordEvent:", event.name, event);
  events.push({ ...event, time: new Date() });
  localStorage.setItem("events", JSON.stringify(events));
  if (events.length >= MAX_LOCAL_EVENTS) {
    flushEvents();
  } else if (!flushTimer) {
    log.debug("setting Background Timer")
    flushTimer = setTimeout(() => {
      flushEvents();
    }, FLUSH_WAIT_TIME);
  }
};

const random = (min = 0, max = 1000000) => {
  let num = Math.random() * (max - min) + min;
  return Math.round(num);
};

// TODO: Add retry logic on analytics/page load events
const callPageLoadApi = ({ path, sessionContext = null, options = {} }) => {
  log.debug(getSessionFromStorage());
  const { document: { referrer }, navigator: { userAgent } } = window;
  pageLoadEvent({ data: { ...options, path, refs, userAgent, referrer }, context: {sessionContext: sessionContext} })
    .catch(err => {
      log.error("Failed to send page load event.");
      log.error(err);
    });
};

const pageLoad = ({ path, hash, sessionContext, options: _options }) => {
  if (!isBrowser()) {
    return;
  }
  if (DISABLE_ANALYTICS_IN_DEV && NODE_ENV === "development") {
    return;
  }
  if (lastPageLoaded === path) {
    return;  // Prevent re-rendering of the same path from triggering this.
  }
  const { advertisingAttribution } = sessionContext;
  const options = {
    ..._options,
    advertisingAttribution,
    adCritterAttribution: advertisingAttribution,
  };
  decodeReferrerHash(hash);
  lastPageLoaded = path;
  log.debug("pageLoad:", path, "referrer:", window.document.referrer);
  if (hasWindowLoaded) {
    callPageLoadApi({ path, sessionContext, options });
  } else {
    // We don't want tracking pixel latency to block page load time.  Therefore, we push them onto an array.
    // They will be fired once the page actually loads.
    log.debug("Initial Page not loaded yet, pushing path:", path);
    pageLoads.push({ path, hash, options });
  }
};

const onWindowLoadEvent = (event) => {
  log.debug("onWindowLoadEvent:", event);
  hasWindowLoaded = true;
  const sessionContext = getSessionFromStorage();
  pageLoads.forEach(({ path, hash, options }) => {
    callPageLoadApi({ path, sessionContext, options });
  });
};

// Called when the Gatsby browser runtime first starts.
const performDeviceIdentification = () => {
  log.debug(`performDeviceIdentification`);
  const deviceIdCookie = getCookie("did");
  if (!deviceIdCookie) {
    log.debug(`No deviceIdCookie cookie found, calling identify`);
    identifyDevice();
  } else {
    log.debug(`deviceIdCookie: ${deviceIdCookie}`);
  }
};

const setPostHogInitialized = () => {
  posthogInitialized = true;
};

// Direct, immediate PostHog capture for callers that need a single event sent
// right away rather than fanned out through the batched recordEvent queue
// (GA/backend/flush timer) -- e.g. a mouseleave-triggered signal. Guarded by
// the same posthogInitialized check as sendEventsToPostHog above, so a call
// before posthog.init() (dev mode -- see the !IS_DEVELOPMENT gate in
// gatsby-browser.js) is dropped instead of hitting an uninitialized instance.
const capturePostHogEvent = (eventName, properties) => {
  if (!posthogInitialized) {
    return;
  }
  posthog.capture(eventName, properties);
};

export {
  recordEvent,
  pageLoad,
  onWindowLoadEvent,
  performDeviceIdentification,
  setPostHogInitialized,
  capturePostHogEvent,
};
